import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ArrowRight, Check } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { US_STATES, VEHICLE_TYPES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  vehicle_type: "",
  pickup_state: "",
  delivery_state: "",
  notes: "",
};

const fieldClass =
  "w-full bg-transparent border-b border-white/20 focus:border-white text-white text-base py-3 placeholder:text-slate-500 transition-colors";

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.vehicle_type || !form.pickup_state || !form.delivery_state) {
      toast.error("Please fill out all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setSubmitted(true);
      toast.success("Quote request received. We'll be in touch shortly.");
      setForm(initial);
      setDate(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="quote-success"
        className="border border-white/10 bg-[#0B132B]/50 p-12 md:p-16 text-center"
      >
        <div className="w-14 h-14 mx-auto mb-6 border border-white/30 flex items-center justify-center">
          <Check className="w-6 h-6 text-white" />
        </div>
        <h3 className="font-display text-3xl md:text-4xl font-semibold text-white mb-4">Quote Received</h3>
        <p className="text-slate-400 max-w-md mx-auto mb-8">
          A Ferrer transport coordinator will contact you within the hour with locked pricing and a
          pickup window. For immediate service, call us directly.
        </p>
        <button
          data-testid="quote-submit-another"
          onClick={() => setSubmitted(false)}
          className="text-[12px] uppercase tracking-[0.25em] text-slate-300 hover:text-white border-b border-white/30 pb-1"
        >
          Submit another quote
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={submit}
      className="border border-white/10 bg-[#0B132B]/40 p-8 md:p-12"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-x-12">
        <div>
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Full Name</label>
          <input
            data-testid="quote-input-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="John Smith"
            className={fieldClass}
            required
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Phone</label>
          <input
            data-testid="quote-input-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(555) 555-5555"
            className={fieldClass}
            required
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Email</label>
          <input
            data-testid="quote-input-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            className={fieldClass}
            required
          />
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Vehicle Type</label>
          <Select value={form.vehicle_type} onValueChange={(v) => update("vehicle_type", v)}>
            <SelectTrigger
              data-testid="quote-select-vehicle"
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-white focus:ring-0 focus:border-white"
            >
              <SelectValue placeholder="Select vehicle" />
            </SelectTrigger>
            <SelectContent className="bg-[#0B132B] border-white/10 text-white">
              {VEHICLE_TYPES.map((v) => (
                <SelectItem key={v} value={v} data-testid={`vehicle-opt-${v}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Preferred Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="quote-date-trigger"
                className="w-full text-left bg-transparent border-b border-white/20 hover:border-white text-base py-3 flex items-center justify-between text-white transition-colors"
              >
                <span className={date ? "text-white" : "text-slate-500"}>
                  {date ? format(date, "PPP") : "Select date"}
                </span>
                <CalendarIcon className="w-4 h-4 text-slate-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#0B132B] border-white/10" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className="text-white"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Pickup State</label>
          <Select value={form.pickup_state} onValueChange={(v) => update("pickup_state", v)}>
            <SelectTrigger
              data-testid="quote-select-pickup"
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-white focus:ring-0 focus:border-white"
            >
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent className="bg-[#0B132B] border-white/10 text-white max-h-64">
              {US_STATES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Delivery State</label>
          <Select value={form.delivery_state} onValueChange={(v) => update("delivery_state", v)}>
            <SelectTrigger
              data-testid="quote-select-delivery"
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-white focus:ring-0 focus:border-white"
            >
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent className="bg-[#0B132B] border-white/10 text-white max-h-64">
              {US_STATES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Notes (Optional)</label>
          <textarea
            data-testid="quote-input-notes"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={3}
            placeholder="Inoperable vehicle, special instructions, etc."
            className={`${fieldClass} resize-none`}
          />
        </div>
      </div>

      <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <p className="text-xs text-slate-500 max-w-md">
          By submitting, you agree to be contacted by phone or email regarding your transport.
          We never share your information.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="quote-submit-button"
          className="group bg-white text-[#050914] px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-semibold hover:bg-slate-200 transition-all flex items-center justify-center gap-3 disabled:opacity-60"
        >
          {submitting ? "Submitting…" : "Request Quote"}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
};

export default QuoteForm;
